/* eslint-disable react/prop-types */
import React from 'react';
import {
    useLocation
} from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';

export {
    default as ErrorBoundary
} from './ErrorBoundary';
export {
    AppErrorFallback, RouteErrorFallback, PanelErrorFallback
} from './fallbacks';
export {
    reportRenderError, RENDER_ERROR_EVENT, __resetRenderErrorReporting
} from './reportRenderError';

/**
 * Tier 1 — mounted once, around the router. Last resort: the shell is gone,
 * so the fallback carries its own.
 */
export function AppErrorBoundary({
    children
}) {
    return (
        <ErrorBoundary name="app-root" variant="app">
            {children}
        </ErrorBoundary>
    );
}

/**
 * Tier 2 — mounted around an <Outlet/> (or a bare route element). The page
 * dies, the header/side menu/studio chrome around it stays usable.
 *
 * Resets on pathname change: without that, one crashed page would keep
 * showing its error after the user clicks a different menu item, because the
 * boundary's state survives the re-render.
 */
export function RouteErrorBoundary({
    name, children, ...rest
}) {
    const {
        pathname
    } = useLocation();
    return (
        <ErrorBoundary
            name={name || 'route'}
            variant="route"
            resetKeys={[pathname]}
            {...rest}
        >
            {children}
        </ErrorBoundary>
    );
}

/**
 * Tier 3 — the high-value one. Builders render arbitrary server-stored JSON,
 * so wrap each panel (and, where it matters, each repeated item) with the id
 * of the record it is rendering. A broken record then costs one inline card
 * instead of the whole builder.
 */
export function PanelErrorBoundary({
    name, title, recordType, recordId, recordLabel, resetKeys, children, ...rest
}) {
    return (
        <ErrorBoundary
            name={name || 'panel'}
            variant="panel"
            title={title}
            recordType={recordType}
            recordId={recordId}
            recordLabel={recordLabel}
            resetKeys={resetKeys || [recordId]}
            {...rest}
        >
            {children}
        </ErrorBoundary>
    );
}

export default ErrorBoundary;
